import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    LinearProgress,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { useNavigate } from 'react-router-dom';
import LinkedToTripButton from './LinkedToTripButton';
import ListEmptyState from './ListEmptyState';

/** Minimal operation shape for list display (logistics or trip detail). */
export interface OperationListItem {
    id: string;
    status?: string | null;
    completedAt?: number | string | null;
    packingTasks?: Array<{ completed?: boolean }> | null;
    /** When present, show "Linked to trip" and navigate to trip on click. */
    trip?: { id: string; name?: string | null } | null;
}

export interface OperationsListSectionProps {
    activeOperations: OperationListItem[];
    completedOperations: OperationListItem[];
    /** (op) => { completed, total, percentage } */
    operationProgress: (op: OperationListItem) => { completed: number; total: number; percentage: number };
    formatSummary: (op: OperationListItem) => string;
    onOperationClick: (op: OperationListItem) => void;
    onCreateOperation?: () => void;
    expandedSections: Set<string>;
    onAccordionChange: (id: string) => (e: React.SyntheticEvent, expanded: boolean) => void;
    /** When true, show trip link chip and navigate to trip on click. */
    showTripLink?: boolean;
    /** When set (e.g. on trip detail), show this message instead of normal empty active state. */
    activeOperationElsewhereMessage?: string;
}

export default function OperationsListSection({
    activeOperations,
    completedOperations,
    operationProgress,
    formatSummary,
    onOperationClick,
    onCreateOperation,
    expandedSections,
    onAccordionChange,
    showTripLink = false,
    activeOperationElsewhereMessage,
}: OperationsListSectionProps) {
    const navigate = useNavigate();

    const handleTripClick = (e: React.MouseEvent, tripId: string) => {
        e.stopPropagation();
        navigate(`/logistics/trip/${tripId}`);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Accordion
                expanded={expandedSections.has('activeOperation')}
                onChange={onAccordionChange('activeOperation')}
                sx={{ boxShadow: 2, '&:before': { display: 'none' } }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Active operation
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    {activeOperationElsewhereMessage ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                            {activeOperationElsewhereMessage}
                        </Typography>
                    ) : activeOperations.length > 0 ? (
                        <List dense sx={{ '& .MuiListItemButton-root': { minHeight: 56 } }}>
                            {activeOperations.map(op => {
                                const { completed, total, percentage } = operationProgress(op);
                                const trip = (op as OperationListItem).trip ?? null;
                                return (
                                    <ListItem key={op.id} disablePadding sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
                                        <ListItemButton onClick={() => onOperationClick(op)}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
                                                <ListItemText
                                                    primary={formatSummary(op)}
                                                    secondary={
                                                        <Box
                                                            component="span"
                                                            sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}
                                                        >
                                                            <span>{`${completed}/${total} tasks (${Math.round(percentage)}%)`}</span>
                                                            {showTripLink && trip?.id && (
                                                                <LinkedToTripButton
                                                                    trip={trip}
                                                                    onClick={e => handleTripClick(e, trip.id)}
                                                                    sx={{ ml: 0.5 }}
                                                                />
                                                            )}
                                                        </Box>
                                                    }
                                                />
                                            </Box>
                                        </ListItemButton>
                                        {total > 0 && (
                                            <LinearProgress
                                                variant="determinate"
                                                value={percentage}
                                                sx={{ height: 6, borderRadius: 1, mx: 2, mb: 1 }}
                                            />
                                        )}
                                    </ListItem>
                                );
                            })}
                        </List>
                    ) : (
                        <ListEmptyState
                            icon={<LocalShippingIcon sx={{ fontSize: 48 }} />}
                            message="No active packing operation"
                            subtext="Create an operation to start moving items between locations"
                            primaryAction={onCreateOperation ? { label: 'Create Operation', onClick: onCreateOperation } : undefined}
                        />
                    )}
                </AccordionDetails>
            </Accordion>

            <Accordion
                expanded={expandedSections.has('completedOperations')}
                onChange={onAccordionChange('completedOperations')}
                sx={{ boxShadow: 2, '&:before': { display: 'none' } }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Completed operations
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    {completedOperations.length > 0 ? (
                        <List dense>
                            {completedOperations.map(op => {
                                const trip = (op as OperationListItem).trip ?? null;
                                return (
                                    <ListItem key={op.id} disablePadding>
                                        <ListItemButton onClick={() => onOperationClick(op)} sx={{ opacity: 0.85 }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                                                    <ListItemText
                                                        primary={formatSummary(op)}
                                                        primaryTypographyProps={{ color: 'text.secondary' }}
                                                        secondary={`Completed ${
                                                            op.completedAt ? new Date(op.completedAt as number).toLocaleDateString() : ''
                                                        }`}
                                                    />
                                                </Box>
                                                {showTripLink && trip?.id && (
                                                    <Box sx={{ pl: 3.5, mt: 0.5 }}>
                                                        <LinkedToTripButton trip={trip} onClick={e => handleTripClick(e, trip.id)} />
                                                    </Box>
                                                )}
                                            </Box>
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                        </List>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No completed operations yet
                        </Typography>
                    )}
                </AccordionDetails>
            </Accordion>
        </Box>
    );
}
